import { FC } from 'react';
import {
  Container,
  Link,
  List,
  ListItem,
  ListItemText,
  Theme,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';

import Meta from '@bizapp-lp/pjr/organisms/Meta';
import { Terms } from '@bizapp-lp/pjr/templates/Terms';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    innerList: {
      '& > li': {
        listStyle: 'none',
        marginLeft: theme.spacing(-4),
      },
    },
  }),
);

const title = 'Project Roomサービス仕様書 | 株式会社ワークスアプリケーションズ';
const description =
  '株式会社ワークスアプリケーションズがご提供する、Project Roomサービス仕様書です。';

const TrialSpec: FC = () => {
  const classes = useStyles();

  const termsProps = {
    pageTitle: (
      <>
        <span style={{ display: 'block' }}>HUE Works Suite</span>
        <span style={{ display: 'block' }}>
          Project Roomトライアルサービス仕様書
        </span>
      </>
    ),
    headerContent: 'Ver.1.1.0',
  };
  return (
    <>
      <Meta>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Meta>
      <Terms {...termsProps}>
        <Container disableGutters maxWidth={false}>
          <Typography>
            これは過去のHUE Works Suite Project
            Roomトライアルサービス仕様書です。最新版は
            <Link component={RouterLink} to="/pjr/trial-spec">
              こちら
            </Link>
            です。
          </Typography>
          <Typography variant="h2">1. 本書の定義</Typography>
          <Typography variant="body1">
            本書は、お客様におけるHUE Works Suite Project Room（以下、Project
            Room）トライアル利用にあたり株式会社ワークスアプリケーションズ・エンタープライズ（以下、弊社）より提供可能なクラウドサービスの内容を記述したものです。Project
            Roomのトライアル利用に際しては、本内容および「HUE Works Suite / HUE
            Works Suite DX Solutions利用規約」に同意していただく必要があります。
          </Typography>
          <Typography variant="body1">
            本書と「HUE Works Suite / HUE Works Suite DX
            Solutions利用規約」の記載内容が異なる場合には利用規約の内容が優先されます。
          </Typography>
          <Typography variant="body1">
            過去に提示済みのサービス内容と内容が異なる場合には、常に最新のサービス内容が優先して適用されます。
          </Typography>
        </Container>

        <Container disableGutters maxWidth={false}>
          <Typography variant="h2">
            2. Project Roomトライアルサービス概要
          </Typography>
          <Typography variant="body1">
            Project
            Roomは法人向けプロジェクトコミュニケーションサービスです。コミュニケーションサービスでよく利用される基本機能に加えファイル管理機能を搭載し、チームやプロジェクトでのコラボレーションによるプロジェクト推進の効率化をサポートします。
          </Typography>
          <Typography variant="body1">
            トライアル利用では、トライアルサービス利用開始日から30日間、最大20ライセンス、利用可能容量200GBまでの範囲でサービスを無料でご利用いただけます。
          </Typography>
          <Typography variant="body1">
            ※本サービスは法人のお客様向けサービスです。ご利用は法人（公的機関を含む）に限らせていただきます。
          </Typography>
          <Typography variant="body1">
            ※本サービスでは外部ユーザーをゲストユーザーとして招待し、共同で利用することが可能です。ただし、当該外部ユーザーによるProject
            Room利用に関連する一切の行為はお客様による行為とみなすこととし、当該外部ユーザーによる利用規約および本サービス仕様書の違反についてはお客様による違反としてお客様が弊社に対して一切の責任を負うものとします。
          </Typography>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.1 標準サービス機能</Typography>
            <Typography variant="body1">
              Project
              Room(トライアル版)では下記機能を提供いたします。あわせて、初期構築作業（利用環境構築作業およびアカウント作成作業）を提供いたします。
            </Typography>
            <Typography variant="body1">
              また、下記機能および初期構築作業の仕様、品質その他一切の内容に関する詳細は、弊社がその裁量によって決定するものとします。
            </Typography>
            <List component="ul">
              <ListItem>
                <ListItemText>管理機能</ListItemText>
                <List component="ul" className={classes.innerList}>
                  <ListItem>
                    <ListItemText>アカウント管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>ゲストアカウント管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>ライセンス管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>権限管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>セキュリティ管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>利用状況モニター</ListItemText>
                  </ListItem>
                </List>
              </ListItem>
              <ListItem>
                <ListItemText>ユーザー機能</ListItemText>
                <List component="ul" className={classes.innerList}>
                  <ListItem>
                    <ListItemText>
                      プロジェクト（プロジェクトに関するやりとりをプロジェクトメンバーと実施するためのスペース）作成
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      各プロジェクトのへ招待可能なゲストを制限するためのメールドメインによる許可リスト設定
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      コミュニケーション（フィード投稿、コメント、いいね）
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      ファイル管理（ファイルアップロード、プレビュー、ダウンロード、フォルダ管理）
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>カレンダー（予定登録）</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      ※ゲストユーザーは招待されたプロジェクト内の機能のみ利用できます。権限管理にて管理者権限を付与することや、プロジェクトの管理者になることはできません。
                    </ListItemText>
                  </ListItem>
                </List>
              </ListItem>
            </List>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">
              2.2 環境構築およびアカウントの発行
            </Typography>
            <Typography variant="body1">
              お申し込みから1週間ほどでお申し込み時に登録いただいたメールアドレスあてに、アクセスURLと初期ログインIDをお送りいたします。
              2ユーザー目以降については、管理者アカウントにてお客様ご自身で登録いただけます。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.3 サポートサービス</Typography>
            <Typography variant="body1">
              お申し込みいただきましたメールアドレスあてに機能マニュアル、FAQおよび問い合わせ先メールアドレスをお送りいたします。ご不明点がありましたら問い合わせ先メールアドレスまでお問い合わせください。3営業日をめどに１次回答いたします。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.4 トライアル期間の終了</Typography>
            <Typography variant="body1">
              無料トライアル期間は、以下いずれか早く到来する日で終了します。
            </Typography>
            <List component="ol">
              <ListItem>
                <ListItemText>
                  トライアルサービス利用開始日から30日後の日
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemText>有償サービスの開始日</ListItemText>
              </ListItem>
              <ListItem>
                <ListItemText>
                  お客様からトライアル利用解約の申込をいただいてから3日後または解約を希望する日のいずれか遅い日
                </ListItemText>
              </ListItem>
            </List>
            <Typography variant="body1">
              トライアル期間終了後、自動的に課金されることはありません。有償サービスの利用を希望される場合は、弊社指定のフォームで有償サービス契約をお申込みください
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.5 提供条件</Typography>
            <Typography variant="body1">
              Project Room（トライアル版）の提供に際しては、
              カレンダー機能内のZoom連携において、Zoom Video Communications,
              Inc.（以下「Zoom」といいます。）が提供するサービスが利用されます。お客様は、Zoom連携を利用するにあたり、Zoom所定の「ZOOM
              TERMS OF
              SERVICE」（その名称又は内容に変更が生じた場合には、当該変更後の名称及び内容によるものとします。
              なお、最新版は
              <Link href="https://zoom.us/terms" target="_blank">
                https://zoom.us/terms
              </Link>
              に掲載されています。）のうちEnd
              Userが遵守すべき事項について同意し、これを遵守するものとします。
            </Typography>
          </Container>
        </Container>
      </Terms>
    </>
  );
};

export default TrialSpec;
